import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  onSnapshot, 
  query, 
  where, 
  Timestamp,
  QuerySnapshot,
  orderBy,
  getDocs
} from 'firebase/firestore';
import { db } from '../firebase/firebase-config';

export interface Document {
  id?: string;
  name: string;
  type: string;
  size: number;
  uploadedAt: string;
  uploadedBy: string;
  url: string;
  category: string;
  description?: string;
  candidateId?: string;
  candidateName?: string;
  data?: any;
  content?: string;
  template?: string;
  jobTitle?: string;
  experience?: any;
  personal?: any;
  skills?: any;
  references?: any;
  customSections?: any;
  [key: string]: any;
}

export interface CVDraft extends Document {
  candidateId: string;
  candidateName: string;
  template: string;
  content: string;
}

const COLLECTION_NAME = 'documents';

// Subscribe to documents with real-time updates (simplified to avoid index requirements)
export const subscribeToDocuments = (
  onUpdate: (documents: Document[]) => void,
  onError?: (error: Error) => void
): (() => void) => {
  console.log('📄 Setting up Firebase real-time listener for documents...');
  
  try {
    const q = collection(db, COLLECTION_NAME);

    const unsubscribe = onSnapshot(q, 
      (snapshot: QuerySnapshot) => {
        console.log('📄 Firebase documents snapshot received, docs count:', snapshot.size);
        
        const documents = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as Document));
        
        // Sort on client side to avoid index requirements
        documents.sort((a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime());
        
        console.log('✅ Processed documents:', documents.length);
        onUpdate(documents);
      },
      (error) => {
        console.error('❌ Firebase documents subscription error:', error);
        if (onError) onError(error);
      }
    );

    return unsubscribe;
  } catch (error: any) {
    console.error('❌ Failed to setup documents subscription:', error);
    if (onError) onError(error);
    return () => {};
  }
};

// Subscribe to documents by category (simplified query without orderBy to avoid index requirements)
export const subscribeToDocumentsByCategory = (
  category: string,
  onUpdate: (documents: Document[]) => void,
  onError?: (error: Error) => void
): (() => void) => {
  console.log(`📄 Setting up Firebase listener for ${category} documents...`);
  
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('category', '==', category)
    );

    const unsubscribe = onSnapshot(q, 
      (snapshot: QuerySnapshot) => {
        console.log(`📄 Firebase ${category} documents snapshot received, count:`, snapshot.size);
        
        const documents = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as Document));
        
        // Sort on client side to avoid index requirements
        documents.sort((a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime());
        
        console.log(`✅ Processed ${category} documents:`, documents.length);
        onUpdate(documents);
      },
      (error) => {
        console.error(`❌ Firebase ${category} documents subscription error:`, error);
        if (onError) onError(error);
      }
    );

    return unsubscribe;
  } catch (error: any) {
    console.error(`❌ Failed to setup ${category} documents subscription:`, error);
    if (onError) onError(error);
    return () => {};
  }
};

// Add a new document
export const addDocument = async (documentData: Omit<Document, 'id'>): Promise<string> => {
  try {
    console.log('📄 Adding document to Firebase:', documentData.name);
    
    const docData = {
      ...documentData,
      uploadedAt: documentData.uploadedAt || new Date().toISOString(),
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };

    const docRef = await addDoc(collection(db, COLLECTION_NAME), docData);
    console.log('✅ Document added with ID:', docRef.id);
    return docRef.id;
  } catch (error: any) {
    console.error('❌ Error adding document:', error);
    throw error;
  }
};

// Update a document
export const updateDocument = async (documentId: string, updates: Partial<Document>): Promise<void> => {
  try {
    console.log('📄 Updating document:', documentId);
    
    const docRef = doc(db, COLLECTION_NAME, documentId);
    await updateDoc(docRef, {
      ...updates,
      updatedAt: Timestamp.now()
    });
    
    console.log('✅ Document updated successfully');
  } catch (error: any) {
    console.error('❌ Error updating document:', error);
    throw error;
  }
};

// Delete a document
export const deleteDocument = async (documentId: string): Promise<void> => {
  try {
    console.log('📄 Deleting document:', documentId);
    
    const docRef = doc(db, COLLECTION_NAME, documentId);
    await deleteDoc(docRef);
    
    console.log('✅ Document deleted successfully');
  } catch (error: any) {
    console.error('❌ Error deleting document:', error);
    throw error;
  }
};

// CV Draft specific functions (simplified query without orderBy to avoid index requirements)
export const subscribeToCVDrafts = (
  onUpdate: (drafts: CVDraft[]) => void,
  onError?: (error: Error) => void
): (() => void) => {
  console.log('📋 Setting up Firebase listener for CV drafts...');
  
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('category', '==', 'cv-drafts')
    );

    const unsubscribe = onSnapshot(q, 
      (snapshot: QuerySnapshot) => {
        console.log('📋 Firebase CV drafts snapshot received, count:', snapshot.size);
        
        const drafts = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as CVDraft));
        
        // Sort on client side to avoid index requirements
        drafts.sort((a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime());
        
        console.log('✅ Processed CV drafts:', drafts.length);
        onUpdate(drafts);
      },
      (error) => {
        console.error('❌ Firebase CV drafts subscription error:', error);
        if (onError) onError(error);
      }
    );

    return unsubscribe;
  } catch (error: any) {
    console.error('❌ Failed to setup CV drafts subscription:', error);
    if (onError) onError(error);
    return () => {};
  }
};

// Find a CV draft by name
const findCVDraftByName = async (name: string): Promise<(CVDraft & { id: string }) | null> => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('category', '==', 'cv-drafts'),
      where('name', '==', name)
    );
    const snapshot = await getDocs(q);
    if (!snapshot.empty) {
      const doc = snapshot.docs[0];
      return { id: doc.id, ...doc.data() } as (CVDraft & { id: string });
    }
    return null;
  } catch (error) {
    console.error('Error finding CV draft by name:', error);
    return null;
  }
};

// Add or update CV draft to prevent duplicates
export const addCVDraft = async (draftData: Partial<CVDraft>): Promise<string> => {
  try {
    // If a valid Firebase ID is provided (and it's not a temporary ID), try to update
    if (draftData.id && !draftData.id.startsWith('autosave_') && !draftData.id.startsWith('manual_')) {
      const docId = draftData.id;
      const { id, ...updateData } = draftData;
      console.log('📋 Updating existing CV draft in Firebase with provided ID:', docId);
      try {
        await updateDocument(docId, updateData);
        return docId;
      } catch (updateError: any) {
        if (updateError.message?.includes('No document to update')) {
          console.log('📋 Document not found, will create a new one');
          // Continue to create new document
        } else {
          throw updateError;
        }
      }
    }

    // Check for existing drafts to prevent duplicates
    if (draftData.candidateId && draftData.name) {
      let existingDraft = null;
      
      // For auto-saved drafts
      if (draftData.name.includes('Auto-saved')) {
        const q = query(
          collection(db, COLLECTION_NAME),
          where('category', '==', 'cv-drafts'),
          where('candidateId', '==', draftData.candidateId),
          where('name', '==', draftData.name)
        );
        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
          existingDraft = snapshot.docs[0];
        }
      }
      // For manual drafts
      else if (draftData.name.includes('Manual Draft')) {
        const q = query(
          collection(db, COLLECTION_NAME),
          where('category', '==', 'cv-drafts'),
          where('candidateId', '==', draftData.candidateId),
          where('name', '==', draftData.name)
        );
        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
          existingDraft = snapshot.docs[0];
        }
      }
      
      if (existingDraft) {
        console.log(`📋 Updating existing CV draft in Firebase: ${draftData.name}`);
        const { id, ...updateData } = draftData;
        await updateDocument(existingDraft.id, updateData);
        return existingDraft.id;
      }
    }
    
    // Otherwise, add a new draft
    console.log('📋 Adding new CV draft to Firebase:', draftData.name);
    
    // remove temporary id if it exists
    const { id, ...docData } = draftData;

    const finalDocData = {
      ...docData,
      category: 'cv-drafts',
      uploadedAt: draftData.uploadedAt || new Date().toISOString(),
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };

    const docRef = await addDoc(collection(db, COLLECTION_NAME), finalDocData);
    console.log('✅ CV draft added with ID:', docRef.id);
    return docRef.id;
  } catch (error: any) {
    console.error('❌ Error adding or updating CV draft:', error);
    throw error;
  }
};

// Delete all CV drafts from Firebase
export const deleteAllCVDrafts = async (): Promise<number> => {
  try {
    console.log('🗑️ Deleting all CV drafts from Firebase...');
    
    const q = query(collection(db, COLLECTION_NAME), where('category', '==', 'cv-drafts'));
    const snapshot = await getDocs(q);
    
    const deletePromises = snapshot.docs.map((docSnapshot) => 
      deleteDoc(doc(db, COLLECTION_NAME, docSnapshot.id))
    );
    
    await Promise.all(deletePromises);
    const deleteCount = snapshot.docs.length;
    console.log(`✅ Deleted ${deleteCount} CV drafts from Firebase`);
    
    return deleteCount;
  } catch (error: any) {
    console.error('❌ Error deleting CV drafts:', error);
    throw error;
  }
};

// Migrate existing localStorage data to Firebase
export const migrateDocumentsToFirebase = async () => {
  // Check if migration has already been done
  const migrationDone = localStorage.getItem('firebase_migration_completed');
  if (migrationDone === 'true') {
    console.log('📋 Firebase migration already completed, skipping...');
    return;
  }

  console.log('🔄 Starting documents migration to Firebase...');
  
  try {
    // Migrate uploaded documents
    const uploadedDocs = localStorage.getItem('uploadedDocuments');
    if (uploadedDocs) {
      const documents = JSON.parse(uploadedDocs);
      console.log(`📤 Migrating ${documents.length} uploaded documents...`);
      
      for (const doc of documents) {
        if (!doc.id || !doc.id.startsWith('fb_')) {
          await addDocument(doc);
        }
      }
    }

    // Migrate CV drafts - but check if they already exist in Firebase first
    const cvDrafts = localStorage.getItem('recruitment_crm_cv_drafts');
    if (cvDrafts) {
      const drafts = JSON.parse(cvDrafts);
      console.log(`📤 Checking ${drafts.length} CV drafts for migration...`);
      
      for (const draft of drafts) {
        if (!draft.id || !draft.id.startsWith('fb_')) {
          // Check if a draft with this name already exists
          if (draft.name) {
            const existingDraft = await findCVDraftByName(draft.name);
            if (!existingDraft) {
              await addCVDraft({
                ...draft,
                category: 'cv-drafts'
              });
            } else {
              console.log(`⏭️ Skipping draft "${draft.name}" - already exists in Firebase`);
            }
          }
        }
      }
    }

    // Mark migration as completed
    localStorage.setItem('firebase_migration_completed', 'true');
    console.log('✅ Documents migration completed and marked');
  } catch (error) {
    console.error('❌ Error migrating documents:', error);
    throw error;
  }
};